// Custom hook for loading and managing PODS data

import { useState, useEffect, useMemo } from 'react';
import { parseCoordinate } from '../utils/formatters';
import { PROVINCES } from '../utils/constants';

/**
 * Custom hook for loading and managing PODS data
 * @returns {Object} PODS data and operations
 */
const usePods = () => {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load PODS data from JSON file
  useEffect(() => {
    const loadPods = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/pods.json');

        if (!response.ok) {
          throw new Error('Failed to load PODS data');
        }

        const data = await response.json();
        setPods(data);
        setError(null);
      } catch (err) {
        console.error('Error loading PODS:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPods();
  }, []);

  // Get unique provinces from PODS data
  const provinces = useMemo(() => {
    const provinceSet = new Set(pods.map((pod) => pod.province));
    return Array.from(provinceSet).map((key) => ({
      key,
      ...PROVINCES[key],
      count: pods.filter((p) => p.province === key).length,
    }));
  }, [pods]);

  // Get unique areas from PODS data
  const areas = useMemo(() => {
    const areaMap = new Map();
    pods.forEach((pod) => {
      if (!areaMap.has(pod.area)) {
        areaMap.set(pod.area, {
          name: pod.area,
          province: pod.province,
          count: 0,
        });
      }
      areaMap.get(pod.area).count++;
    });
    return Array.from(areaMap.values());
  }, [pods]);

  // Get PODS by province
  const getPodsByProvince = (provinceKey) => {
    return pods.filter((pod) => pod.province === provinceKey);
  };

  // Get PODS by area
  const getPodsByArea = (areaName) => {
    return pods.filter((pod) => pod.area === areaName);
  };

  // Get a single POD by site number
  const getPodBySiteNumber = (siteNumber) => {
    return pods.find((pod) => pod.siteNumber === siteNumber);
  };

  // Filter PODS by various criteria
  const filterPods = (filters = {}) => {
    let filtered = [...pods];

    if (filters.province) {
      filtered = filtered.filter((pod) => pod.province === filters.province);
    }

    if (filters.area) {
      filtered = filtered.filter((pod) => pod.area === filters.area);
    }

    if (filters.status) {
      filtered = filtered.filter((pod) => pod.status === filters.status);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (pod) =>
          pod.siteNumber.toLowerCase().includes(searchLower) ||
          pod.area.toLowerCase().includes(searchLower) ||
          pod.streetAddress.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  // Get statistics
  const stats = useMemo(() => ({
    total: pods.length,
    byProvince: provinces.reduce((acc, p) => {
      acc[p.key] = p.count;
      return acc;
    }, {}),
    byStatus: pods.reduce((acc, pod) => {
      acc[pod.status] = (acc[pod.status] || 0) + 1;
      return acc;
    }, {}),
  }), [pods, provinces]);

  return {
    pods,
    provinces,
    areas,
    loading,
    error,
    stats,
    getPodsByProvince,
    getPodsByArea,
    getPodBySiteNumber,
    filterPods,
  };
};

export default usePods;
